/**
 * Opening an EDITOR makes a document (RFC 0275 §4).
 *
 * The first version of this was an explanatory card: "there is nothing to open
 * here, open a text/markdown document and this edits it." True, and useless.
 * Clicking TextEdit gives you a blank document; nobody wants to be told that the
 * editor is technically a relationship between an app and a type. Selecting the
 * editor IS New, the same rule RFC 0271 already applies to document apps.
 *
 * The difference from NewInstanceGate is what gets made. That one mints a typed
 * INSTANCE of an app's own dataType; this mints an ORDINARY DOCUMENT of the mime
 * the app declares it handles. The editor owns no type and no records — it edits
 * documents anyone can make, which is exactly why `handles` and `dataType` are
 * different fields.
 *
 * REUSE, not just minting, for the same reason as instances: clicking an editor
 * twice should not litter the project with untouched blank files. If the
 * newest document of that type is still empty, that one is handed back.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  createDocument,
  type DocumentRef,
  readDocumentContent,
} from '@mod/core/model'
import { useEffect, useRef, useState } from 'react'
import { personChangeMessage } from '../../services/identity/localPerson'
import { useDocument } from '../../services/world/repo/useDocument'

/** Newest first, so "the most recent document" has one meaning. */
function newestFirst(docs: DocumentRef[]): DocumentRef[] {
  return [...docs].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
}

/**
 * A name that does not collide, without asking.
 *
 * `Untitled`, then `Untitled 2`. Numbered off what exists rather than a random
 * suffix: a person reading their sidebar should see something they could have
 * typed, and `notes/8s7nz0h9.md` is not that.
 */
export function nextUntitledPath(
  existing: DocumentRef[],
  extension: string,
): string {
  const taken = new Set(existing.map((d) => d.path))
  const base = 'Untitled'
  if (!taken.has(`${base}${extension}`)) return `${base}${extension}`
  for (let n = 2; n < 500; n++) {
    const candidate = `${base} ${n}${extension}`
    if (!taken.has(candidate)) return candidate
  }
  return `${base} ${Date.now()}${extension}`
}

/** The file extension a mime should be written with. */
export function extensionFor(mime: string): string {
  if (mime === 'text/markdown') return '.md'
  if (mime === 'text/html') return '.html'
  if (mime === 'application/json') return '.json'
  return '.txt'
}

export function NewDocumentGate({
  appName,
  mime,
  projectId,
  documents,
  onOpen,
}: {
  appName: string
  /** The type this editor handles, and therefore the type it creates. */
  mime: string
  projectId: string
  /** Every document of that mime, for the reuse rule and for naming. */
  documents: DocumentRef[]
  onOpen: (documentId: string) => void
}) {
  const repo = useRepo() as unknown as Repo
  const [failure, setFailure] = useState<string | null>(null)
  // The id we made, held until the CATALOG has it. `onOpen` routes by id
  // through the shell's file resolution, which reads the project catalog —
  // and a document created a moment ago is not in this client's copy yet. Firing
  // immediately resolves to nothing, the request is dropped, and the gate sits
  // on "Opening…" forever with the document existing and unreachable.
  const [created, setCreated] = useState<string | null>(null)

  const newest = newestFirst(documents)[0]
  const [newestDoc] = useDocument(newest?.id as AnyDocumentId | undefined)

  // Once per mount. Without the ref this mints on every render, and React's
  // development double-invoke alone would create two documents per click.
  const done = useRef(false)

  useEffect(() => {
    if (done.current) return
    // Wait for the reuse candidate to hydrate, or the first decision is always
    // "mint" and the reuse rule never fires.
    if (newest && newestDoc === undefined) return
    done.current = true

    if (newest && newestDoc !== undefined && !readDocumentContent(newestDoc)) {
      onOpen(newest.id)
      return
    }
    void createDocument(repo, projectId, {
      ...personChangeMessage(),
      path: nextUntitledPath(documents, extensionFor(mime)),
      mime,
      content: '',
    })
      .then((handle) => setCreated(handle.documentId as string))
      .catch((e) => {
        done.current = false
        setFailure(e instanceof Error ? e.message : String(e))
      })
  }, [repo, projectId, mime, documents, newest, newestDoc, onOpen])

  // Route once the catalog can see it. `documents` is projected from the
  // project doc, so this fires on the sync that makes the id resolvable.
  useEffect(() => {
    if (!created) return
    if (!documents.some((d) => d.id === created)) return
    onOpen(created)
  }, [created, documents, onOpen])

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-6">
      {failure ? (
        <p className="max-w-sm text-center text-destructive text-sm">
          Could not create a document for {appName}: {failure}
        </p>
      ) : (
        <p className="text-muted-foreground/60 text-sm">Opening {appName}…</p>
      )}
    </div>
  )
}
